import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import useAccess from "../hooks/useAccess";
import { resolveRoutePermission } from "../config/permissionRegistry";
import { notifyPermissionDenied } from "./PermissionFeedback";

const EDIT_TEXT = /^(save|save changes?|edit|update|create|add(?: new)?|assign|submit|generate|change)(\b|\s)/i;
const DELETE_TEXT = /^(delete|remove)\b/i;

const controlText = (element) =>
  [
    element.textContent,
    element.getAttribute("title"),
    element.getAttribute("aria-label"),
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

const isDeleteControl = (element) =>
  element.matches(".is-delete, [data-permission-action='delete']") ||
  Boolean(element.querySelector(".fa-trash, .fa-trash-alt")) ||
  DELETE_TEXT.test(controlText(element));

const isEditControl = (element) =>
  element.matches(".is-edit, [data-permission-action='edit']") ||
  Boolean(element.querySelector(".fa-edit, .fa-save, .fa-plus")) ||
  EDIT_TEXT.test(controlText(element));

export default function GlobalPermissionGuard() {
  const location = useLocation();
  const { hasPermission, hasAnyPagePermission, permissionsLoaded } = useAccess();
  const isAdmin = localStorage.getItem("userRole") === "1";
  const permission = resolveRoutePermission(location.pathname, location.search);
  const canEdit =
    !isAdmin ||
    !permission ||
    (permission.resource === "*"
      ? hasAnyPagePermission(permission.page, "edit")
      : hasPermission(permission.page, permission.resource, "edit"));
  const canDelete =
    isAdmin &&
    Boolean(permission) &&
    (permission.resource === "*"
      ? hasAnyPagePermission(permission.page, "delete")
      : hasPermission(permission.page, permission.resource, "delete"));

  useEffect(() => {
    if (!permissionsLoaded || !isAdmin || !permission) return undefined;

    const apply = () => {
      document.querySelectorAll("button, a[role='button']").forEach((element) => {
        if (element.closest(".permission-toast")) return;
        if (isDeleteControl(element)) {
          element.toggleAttribute("hidden", !canDelete);
          element.dataset.permissionHidden = canDelete ? "false" : "true";
          return;
        }
        if (isEditControl(element) && !canEdit) {
          element.dataset.permissionBlocked = "true";
          element.setAttribute("aria-disabled", "true");
          element.title = `No permission to edit ${permission.resource}.`;
        } else if (element.dataset.permissionBlocked === "true") {
          delete element.dataset.permissionBlocked;
          element.removeAttribute("aria-disabled");
        }
      });
    };

    const block = (event) => {
      const control = event.target.closest?.("button, a[role='button']");
      if (!control || control.dataset.permissionBlocked !== "true") return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      notifyPermissionDenied(`No permission to edit ${permission.resource}.`);
    };

    apply();
    const observer = new MutationObserver(apply);
    observer.observe(document.body, { childList: true, subtree: true });
    document.addEventListener("click", block, true);
    return () => {
      observer.disconnect();
      document.removeEventListener("click", block, true);
    };
  }, [canDelete, canEdit, isAdmin, permission, permissionsLoaded]);

  return null;
}
